<template>
  <div class="consign">
    <div class="video">
      <!-- Stávající video -->
      <div v-if="currentVideo" class="current-video">
        <p> <i class="video-icon">🎬</i>Actual video:
          <a :href="`/static/videos/${currentVideo}`"  class="video-url">
            {{ currentVideo }}</a>
          <video :src="`/static/videos/${currentVideo}`" controls :key="currentVideo">
          </video>
        </p>
      </div>

      <!-- Formulář pro nahrání nového videa -->
      <div class="upload-section">
        <p v-if="videoRequired" class="video-required">
          <i class="video-icon">🎬</i>Video is required!
        </p>
        <p v-else class="video-optional"><i class="video-icon">🎬</i>Video is optional.</p>
        <button v-if="currentVideo" @click="removeVideo" class="remove-btn">Remove video</button>
        <label for="video-upload">Upload new video (max size is 64MB):</label>
        <input type="file" id="video-upload" ref="videoInput" accept="video/*" @change="handleFileChange"
          class="file-input" />
        <div class="controls">
          <button @click="uploadVideo" class="upload-btn" :disabled="!selectedFile || uploading">
            {{ uploading ? 'uploading...' : 'Upload video' }}
          </button>
          <button v-if="selectedFile" @click="cancelSelection" class="cancel-btn">
            Zrušit výběr
          </button>
        </div>

        <!-- Progress bar při nahrávání -->
        <div v-if="uploading" class="progress-container">
          <div class="progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
          <span class="progress-text">{{ uploadProgress }}%</span>
        </div>

        <!-- Informace o souboru -->
        <div v-if="selectedFile" class="file-info">
          <p>Selected file: {{ selectedFile.name }}</p>
          <p>Size: {{ formatFileSize(selectedFile.size) }}</p>
        </div>
      </div>
    </div>

    <!-- Zobrazení chyb -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { api } from '@/utils/axios'
import { useMessagesStore } from '@/stores/messages'
import confirmDialog from '@/utils/confirmDialog'


const props = defineProps({
  projectId: {
    type: [Number, String],
    required: true,
  },
  videoFile: {
    type: String,
    default: null,
  },
  videoRequired: {
    type: Boolean,
    default: true,
  }
})

const emit = defineEmits(['video-updated'])

const messages = useMessagesStore()
const videoInput = ref(null)
const selectedFile = ref(null)
const uploading = ref(false)
const uploadProgress = ref(0)
const error = ref('')
const videoUrl = ref('')
const currentVideo = ref(props.videoFile)
const videoRequired = ref(props.videoRequired)

// Formátování velikosti souboru
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB'
  else return (bytes / 1048576).toFixed(2) + ' MB'
}

// Obsluha změny souboru
const handleFileChange = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Kontrola typu souboru
  if (!file.type.startsWith('video/')) {
    error.value = 'Prosím vyberte video soubor.'
    return
  }

  // check max size)
  if (file.size > 64 * 1024 * 1024) {
    error.value = 'File is too large. Max size is 64MB.'
    return
  }

  selectedFile.value = file
  error.value = ''
}

async function removeVideo() {
    const confirmed = await confirmDialog.confirm({
    title: 'Video remove?',
    message: 'Realy remove this video?',
    confirmText: 'Yes',
    cancelText: 'No',
  })
  if (!confirmed) return

  try {
    const response = await api.post(`/remove-video/${props.projectId}`)
    messages.info('👍 ' + response.data.msg)
    currentVideo.value = null
    emit('video-updated', null)

    await api.get(`/project/${props.projectId}`)
  } catch (error) {
    messages.error('❌ Error: ' + (error.response?.data?.msg || error.message))
  }
}

const uploadVideo = async () => {
  if (!selectedFile.value) return

  uploading.value = true
  uploadProgress.value = 0

  const formData = new FormData()
  formData.append('file', selectedFile.value)
  formData.append('project_id', props.projectId)

  try {
    // Implementace nahrávání s progress barem
    const response = await api.post('/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      },
    })

    // Aktualizace UI po úspěšném nahrání
    currentVideo.value = response.data.video_file
    selectedFile.value = null
    videoInput.value.value = ''

    // Informování uživatele
    messages.success(response.data.msg)

    // Emitování události pro rodiče
    emit('video-updated', response.data.video_file)
  } catch (err) {
    error.value = err.response?.data?.msg || 'Chyba při nahrávání videa.'
    messages.error(error.value)
  } finally {
    uploading.value = false
  }
}

// Uložení URL odkazu na video
const saveVideoUrl = async () => {
  if (!videoUrl.value || videoUrl.value === currentVideo.value) return

  try {
    const response = await api.post(`/project/set/${props.projectId}`, {
      video_file: videoUrl.value,
    })

    currentVideo.value = videoUrl.value
    messages.success('URL videa bylo uloženo.')
    emit('video-updated', videoUrl.value)
  } catch (err) {
    error.value = err.response?.data?.msg || 'Chyba při ukládání URL videa.'
    messages.error(error.value)
  }
}

// Zrušení výběru souboru
const cancelSelection = () => {
  selectedFile.value = null
  videoInput.value.value = ''
}

// Inicializace při načtení komponenty
onMounted(() => {
  if (props.videoFile) {
    currentVideo.value = props.videoFile
  }
})
</script>

<style scoped>


video {
  max-width: 98%;
  max-height: 444px;

}

.consign {
  margin: 1.5rem 0;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
}

h3 {
  margin-top: 0;
  color: #333;
  font-size: 1.25rem;
}

.video {
  display: flex;
  gap: 1rem;
}

.current-video {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f0f0f0;
  border-radius: 6px;
  flex: 1;
}

.upload-section {
  margin-top: 1rem;
  flex: 1;
}

.video-link {
  align-items: center;
  word-break: break-all;
}

.video-url {
  color: #6655aa;
  text-decoration: none;
  transition: color 0.2s;
  align-items: center;
}

.video-url:hover {
  color: #4a3d7d;
  text-decoration: underline;
}

.video-icon {
  margin-left: 8px;
  font-style: normal;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.file-input {
  margin-bottom: 1rem;
  padding: 0.5rem;
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.upload-btn,
.save-btn {
  padding: 0.5rem 1rem;
  background-color: #6655aa;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.upload-btn:hover,
.save-btn:hover {
  background-color: #4a3d7d;
}

.upload-btn:disabled,
.save-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 0.5rem 1rem;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-btn:hover {
  background-color: #d32f2f;
}

.progress-container {
  margin: 1rem 0;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  line-height: 20px;
  color: white;
  font-size: 0.8rem;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.file-info {
  margin: 1rem 0;
  padding: 0.5rem;
  background-color: #e3f2fd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.file-info p {
  margin: 0.25rem 0;
}

.video-url-input {
  margin-top: 1.5rem;
  border-top: 1px solid #ddd;
  padding-top: 1.5rem;
}

.url-input {
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.error-message {
  margin-top: 1rem;
  padding: 0.5rem;
  color: #f44336;
  background-color: #ffebee;
  border-radius: 4px;
  border-left: 4px solid #f44336;
}

.remove-btn {
  margin: 1rem;
  padding: 0.3rem 0.4rem;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

</style>
